// BHP-1083: one place that turns a thrown error into text we are willing to
// show a user. `ApiError.message` is the technical string ("422 An error
// occurred: …") built for logs — never render it. RFC 7807 `detail` carries the
// backend's human-readable reason, `title` is the fallback, and 422 responses
// may instead carry field-level `violations`.
//
// BHP-1144: and `detail` is only trustworthy for a 4xx. On a 5xx or a
// transport failure it is a technical/third-party string ("Hetzner refused to
// create the server: [403] …") that must never reach a user — those get the
// caller's own fallback, never the backend's words. A reference id is added
// only where support can find it: when Backend2.0 itself answered.
import { ApiError } from './ApiError';

export interface FormErrors {
  /** Messages that belong next to an input, keyed by field name. */
  fields: Record<string, string>;
  /** What is left for the form-level banner; null when every violation found a field. */
  message: string | null;
}

/**
 * BHP-1144: appended to a generic message on an unexpected failure, so the
 * user has something to quote to support and support has something to search.
 * Kept out of the fallback strings themselves so callers only write the
 * friendly part.
 */
function withReference(message: string, error: ApiError): string {
  // Only Backend2.0 tags its logs and Sentry with the id. For a transport
  // failure, an invalid body or a proxy error page nobody can ever look it
  // up, and a reference that leads nowhere is worse than none.
  return error.requestId === '' || !error.fromBackend
    ? message
    : `${message} If the problem persists, contact support with reference ${error.requestId}.`;
}

/**
 * BHP-1048: split a failed submit into per-field messages and a banner.
 *
 * `formFields` is what the form actually renders. A violation whose
 * `propertyPath` is in that list is bound to the input; anything else — a field
 * the form does not show, or a class-level constraint — goes into the banner
 * instead of being dropped. That last part matters: the backend validates DTO
 * properties the form may not have (`CreateDnsRecordDto.dnsZoneUuid` is set by
 * the panel, not typed by the user), and a 422 that renders as nothing at all
 * looks to the user like a button that silently does not work.
 *
 * Unbound messages keep their property path. It is a technical name and not
 * pretty, but it is the only clue to which value the backend rejected, and this
 * branch only fires when form and DTO have drifted apart — which is a bug worth
 * being able to read off the screen.
 */
export function toFormErrors(
  error: unknown,
  fallback: string,
  formFields: readonly string[]
): FormErrors {
  // React Query hands you `mutation.error === null` while everything is fine,
  // so "no error" has to mean "nothing to show" — otherwise every untouched
  // form would render the fallback banner on first paint.
  if (error === null || error === undefined) {
    return { fields: {}, message: null };
  }
  if (!(error instanceof ApiError)) {
    return { fields: {}, message: fallback };
  }
  // BHP-1144: the same rule as toUserMessage — violations on a 5xx come from
  // a proxy or a broken handler and are not meant for users.
  if (error.isUnexpected()) {
    return { fields: {}, message: toUserMessage(error, fallback) };
  }

  const bindable = new Set(formFields);
  const fields: Record<string, string> = {};
  const unbound: string[] = [];

  for (const violation of error.violations) {
    if (bindable.has(violation.propertyPath)) {
      // First message per field wins: an input has room for one line, and the
      // first constraint to fail is the one the user has to fix first anyway.
      if (fields[violation.propertyPath] === undefined) {
        fields[violation.propertyPath] = violation.message;
      }
      continue;
    }
    unbound.push(
      violation.propertyPath === ''
        ? violation.message
        : `${violation.propertyPath}: ${violation.message}`
    );
  }

  if (unbound.length > 0) {
    return { fields, message: unbound.join(' ') };
  }
  if (error.violations.length > 0) {
    return { fields, message: null };
  }

  return { fields, message: toUserMessage(error, fallback) };
}

export function toUserMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) {
    return fallback;
  }
  // BHP-1144: a 5xx or transport failure never shows the backend's `detail`
  // (a technical/third-party string). The caller's fallback is the friendly,
  // feature-specific message; a reference id ties it to the logs.
  if (error.isUnexpected()) {
    return withReference(fallback, error);
  }
  if (error.violations.length > 0) {
    return error.violations.map((violation) => violation.message).join(' ');
  }
  const reason =
    error.detail !== ''
      ? error.detail
      : error.title !== ''
        ? error.title
        : fallback;
  // A 422 without violations is a refusal no field edit fixes ("This server is
  // not fully set up yet."); the reference lets support find the cause.
  return error.status === 422 ? withReference(reason, error) : reason;
}
